import React, { useState, useEffect, useRef } from "react";
//import ReactPlayer from "react-player";
import { Player } from "react-tuby";
import axios from "axios";
import "../../pages/styles.css";
import { API_BASE, VIDEO_HOST } from "../../config";

const API = API_BASE;
const POLL_INTERVAL_MS = 4000;
// How long we wait on the on-demand encoder before we stop pretending it's a
// quick wait. The backend queue can stall (a video sits at "pending" forever),
// which used to leave the user staring at an infinite spinner. After this we
// keep polling (a late encode still auto-plays) but show a clearer message,
// the elapsed time, a retry button, and any mirror link.
const ENCODE_SLOW_AFTER_MS = 90 * 1000;

function formatSubLabel(caption) {
  var label = caption.label;
  if (caption.type === "cp") label += " (official)";
  if (caption.type === "auto") label += " (auto)";
  if (caption.type === "fan") label += " (fan)";
  if (caption.fanName !== "") label += ` by ${caption.fanName}`;
  return label;
}

function formatElapsed(ms) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Channels that still have a CDN bucket serve a pre-encoded static file, so we
// can point the player straight at it. Channels without a bucket use the newer
// on-demand encoding flow (request -> status -> download) handled below.
function getDirectUrl(post, channel) {
  if (channel.bucket) {
    // mirror channelUrl fallback used everywhere else in the app
    const alias = channel.channelAlias || channel.channelCode;
    return `https://${channel.bucket}.${VIDEO_HOST}/${alias}/${post.postId}/${post.postId}-video.mp4`;
  }
  // Legacy ready-to-play mirror, if the API supplied one.
  if (post?.alt_url?.mirror) return post.alt_url.mirror;
  return null;
}

// A user-openable alternative source for when on-demand encoding is stuck.
// The API exposes Google Drive copies via `mirrors[]`; the /file/d/<id>/view
// page plays reliably in Google's own viewer (a raw uc?export=download link
// hits the large-file virus-scan interstitial, so we link the viewer instead).
function getMirrorUrl(post) {
  const gdrive = post?.mirrors?.find((m) => m && m.type === "gdrive" && m.file_id);
  if (gdrive) return `https://drive.google.com/file/d/${gdrive.file_id}/view`;
  if (post?.alt_url?.mirror) return post.alt_url.mirror;
  return null;
}

const StatusPanel = ({ state, percent, message, onRetry, thumbnail_url, elapsedMs, slow, mirrorUrl }) => {
  const titleByState = {
    loading: "영상 정보를 불러오는 중…",
    encoding: slow ? "영상 준비가 길어지고 있어요" : "영상을 준비하고 있습니다",
    error: "영상을 재생할 수 없습니다",
    notfound: "영상을 찾을 수 없습니다",
  };

  const mirrorLink = mirrorUrl && (
    <a
      href={mirrorUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        marginTop: "4px",
        padding: "8px 20px",
        background: "rgba(255,255,255,0.12)",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.4)",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "14px",
        textDecoration: "none",
      }}
    >
      Google Drive에서 보기
    </a>
  );

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        background: thumbnail_url
          ? `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('${thumbnail_url}') center/cover no-repeat`
          : "#1a1a1d",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        textAlign: "center",
        padding: "24px",
        boxSizing: "border-box",
        borderRadius: "4px",
      }}
    >
      <strong style={{ fontSize: "18px", marginBottom: "10px" }}>
        {titleByState[state] || "잠시만 기다려주세요"}
      </strong>

      {state === "encoding" && (
        <>
          <p style={{ fontSize: "13px", opacity: 0.85, margin: "0 0 14px", maxWidth: "420px", lineHeight: 1.5 }}>
            {slow
              ? "변환 서버가 혼잡하거나 대기가 길어지고 있어요. 이 화면을 열어두시면 준비되는 대로 자동 재생됩니다. 다시 시도하거나, 가능하면 아래 대체 링크로 바로 시청할 수 있어요."
              : mirrorUrl
              ? "처음 재생하는 영상은 변환에 다소 시간이 걸릴 수 있어요. 준비되는 대로 자동 재생되며, 기다리지 않고 아래 Google Drive 링크로 바로 시청할 수도 있어요."
              : "처음 재생하는 영상은 변환에 다소 시간이 걸릴 수 있어요. 이 화면을 유지하시면 준비되는 대로 자동으로 재생됩니다."}
          </p>
          <div style={{ width: "60%", maxWidth: "320px", height: "6px", background: "rgba(255,255,255,0.25)", borderRadius: "3px", overflow: "hidden" }}>
            <div
              style={{
                width: percent != null ? `${percent}%` : "100%",
                height: "100%",
                background: "#8D54E6",
                transition: "width 0.4s ease",
                ...(percent == null ? { animation: "vlivearkIndeterminate 1.2s ease-in-out infinite" } : {}),
              }}
            />
          </div>
          <span style={{ fontSize: "13px", marginTop: "8px", opacity: 0.9 }}>
            {percent != null ? `${percent}%` : "변환 대기 중…"}
            {elapsedMs >= 1000 ? ` · ${formatElapsed(elapsedMs)} 경과` : ""}
          </span>

          {(slow || mirrorUrl) && (
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center", marginTop: "16px" }}>
              {slow && onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  style={{
                    padding: "8px 20px",
                    background: "#8D54E6",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  다시 시도
                </button>
              )}
              {mirrorLink}
            </div>
          )}
        </>
      )}

      {(state === "error" || state === "notfound") && (
        <>
          {message && (
            <p style={{ fontSize: "13px", opacity: 0.85, margin: "0 0 14px", maxWidth: "420px", lineHeight: 1.5 }}>
              {message}
            </p>
          )}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
            {state === "error" && onRetry && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  marginTop: "4px",
                  padding: "8px 20px",
                  background: "#8D54E6",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                다시 시도
              </button>
            )}
            {mirrorLink}
          </div>
        </>
      )}

      <style>{`@keyframes vlivearkIndeterminate {0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}`}</style>
    </div>
  );
};

const VideoPlayer = ({ post, channel, thumbnail_url }) => {
  const subtitles = post?.captions || [];
  const videoSeq = post?.officialVideo?.videoSeq;
  const directUrl = getDirectUrl(post, channel);
  const mirrorUrl = getMirrorUrl(post);

  const [videoUrl, setVideoUrl] = useState(directUrl);
  // loading | encoding | ready | error | notfound
  const [state, setState] = useState(directUrl ? "ready" : "loading");
  const [percent, setPercent] = useState(null);
  const [message, setMessage] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  const pollRef = useRef(null);
  const requestedRef = useRef(false);
  const encodeStartRef = useRef(null);

  useEffect(() => {
    if (directUrl) return; // bucket / mirror channels already have a URL
    if (!videoSeq) {
      setState("notfound");
      return;
    }

    let cancelled = false;
    requestedRef.current = false;
    encodeStartRef.current = null;
    setState("loading");
    setPercent(null);
    setElapsedMs(0);

    const clearPoll = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    // Enter/stay in the encoding state, stamping when the wait began so the
    // panel can show elapsed time and switch to the "taking longer" UI.
    const markEncoding = (p) => {
      if (encodeStartRef.current == null) encodeStartRef.current = Date.now();
      setPercent(p);
      setState("encoding");
    };

    const apply = (data) => {
      if (cancelled || !data) return;
      switch (data.status) {
        case "available":
          setVideoUrl(data.url || `${API}/download/${videoSeq}.mp4`);
          setState("ready");
          clearPoll();
          break;
        case "available_to_request":
          // Not requested yet — trigger encoding once, then keep polling.
          // The request endpoint only accepts GET (a POST returns 405, which
          // would be swallowed below and leave the video stuck "준비 중" forever).
          if (!requestedRef.current) {
            requestedRef.current = true;
            axios.get(`${API}/video/${videoSeq}/request`).catch(() => {
              // Let the next poll retry instead of getting stuck permanently.
              requestedRef.current = false;
            });
          }
          markEncoding(null);
          break;
        case "pending":
        case "queued":
          markEncoding(null);
          break;
        case "in_progress":
          markEncoding(typeof data.percentDone === "number" ? data.percentDone : null);
          break;
        case "failed":
          setMessage("영상 변환에 실패했습니다. 잠시 후 다시 시도해주세요.");
          setState("error");
          clearPoll();
          break;
        case "video_not_found":
          setState("notfound");
          clearPoll();
          break;
        default:
          break;
      }
    };

    const check = () => {
      axios
        .get(`${API}/video/${videoSeq}/status`)
        .then((res) => apply(res.data))
        .catch(() => {
          if (cancelled) return;
          setMessage("영상 상태를 불러오지 못했습니다. 네트워크를 확인해주세요.");
          setState("error");
          clearPoll();
        });
    };

    check();
    pollRef.current = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [videoSeq, directUrl, attempt]);

  // Tick elapsed time while encoding so the wait never looks frozen and we can
  // flip to the "taking longer" UI once it crosses the threshold.
  useEffect(() => {
    if (state !== "encoding") return;
    const id = setInterval(() => {
      if (encodeStartRef.current != null) {
        setElapsedMs(Date.now() - encodeStartRef.current);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [state]);

  const handleRetry = () => {
    setMessage("");
    requestedRef.current = false;
    encodeStartRef.current = null;
    setElapsedMs(0);
    // Re-kick the encoder. For an orphaned "pending" job the in-effect trigger
    // never fires (it only requests on "available_to_request"), so do it here.
    if (videoSeq && !directUrl) {
      axios.get(`${API}/video/${videoSeq}/request`).catch(() => {});
    }
    setState("loading");
    setAttempt((a) => a + 1);
  };

  const slow = state === "encoding" && elapsedMs >= ENCODE_SLOW_AFTER_MS;

  if (state === "ready" && videoUrl) {
    return (
      //<ReactPlayer
      //  url={video_url}
      //  height="400px"
      //  width="100%"
      //  controls={true}
      //  config={{ file: { attributes: { crossOrigin: true } } }}
      ///>
      <Player
        src={videoUrl}
        poster={thumbnail_url}
        subtitles={subtitles.map((e) => ({
          url: `/subtitles/${e.file_name}`,
          lang: e.language,
          language: formatSubLabel(e),
        }))}
      />
    );
  }

  return (
    <StatusPanel
      state={state}
      percent={percent}
      message={message}
      thumbnail_url={thumbnail_url}
      elapsedMs={elapsedMs}
      slow={slow}
      mirrorUrl={mirrorUrl}
      onRetry={handleRetry}
    />
  );
};

export default VideoPlayer;
